import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged, skip, tap } from 'rxjs/operators';
import { merge, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { CompanyNotificationsDataSource } from '../../../../core/management/_data-sources/company-notification.datasource';
import { CompanyNotificationModel } from '../../../../notification/models/company-notification.model';
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../core/_base/crud';
import { AppState } from '../../../../core/reducers';
import { selectCompanyNotificationError } from '../../../../core/management/_selectors/company-notification.selectors';
import { CompanyNotificationsPageRequested } from '../../../../core/management/_actions/company-notification.actions';
import { CompanyNotificationEditComponent } from './company-notification-edit/company-notification-edit.component';
import { CompanyNotificationService } from 'src/app/notification/services/company-notification.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-company-notification-list',
  templateUrl: './company-notification-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyNotificationListComponent implements OnInit, OnDestroy {
  dataSource: CompanyNotificationsDataSource;
  displayedColumns = ['description', 'source', 'channels', 'status', 'action'];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  companyNotificationsResult: CompanyNotificationModel[] = [];
  private subscriptions: Subscription[] = [];
  id;
  level;

  constructor(
    public dialogRef: MatDialogRef<CompanyNotificationListComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private companyNotificationsService: CompanyNotificationService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadCompanyNotificationsList()))
        .subscribe(),
    );

    this.dataSource = new CompanyNotificationsDataSource(this.store);
    this.subscriptions.push(
      this.dataSource.entitySubject.pipe(skip(1), distinctUntilChanged()).subscribe((res) => {
        this.companyNotificationsResult = res;
      }),
    );
    this.loadCompanyNotificationsList();
    this.subscriptions.push(
      this.store.pipe(select(selectCompanyNotificationError), skip(1)).subscribe((res) => {
        if (res) {
          this.layoutUtilsService.showActionNotification(
            this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
            MessageType.Update,
            3000,
            true,
            false,
          );
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => el.unsubscribe());
  }

  loadCompanyNotificationsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction,
      this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    this.store.dispatch(
      new CompanyNotificationsPageRequested({
        page: queryParams,
        id: this.data.related_id,
        level: this.data.level,
      }),
    );
  }

  filterConfiguration(): any {
    const filter: any = {};
    return filter;
  }

  deleteCompanyNotification(_item: CompanyNotificationModel) {
    const _title: string = this.translate.instant('OFFICE.DELETE_OFFICE_SIMPLE.TITLE');
    const _description: string = this.translate.instant('OFFICE.DELETE_OFFICE_SIMPLE.DESCRIPTION');
    const _waitDescription: string = this.translate.instant(
      'OFFICE.DELETE_OFFICE_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.companyNotificationsService.deleteCompanyNotification(_item.id).subscribe(() => {
        this.loadCompanyNotificationsList();
      });
    });
  }

  updateCompanyNotification(companyNotification: CompanyNotificationModel) {
    const refDialog = this.dialog.open(CompanyNotificationEditComponent, {
      data: {
        companyNotification,
        level: this.data.level,
        related_id: this.data.related_id,
      },
      width: '600px',
    });
    refDialog.afterClosed().subscribe(() => {
      this.loadCompanyNotificationsList();
    });
  }

  getItemCssClassByStatus(isActive = false): string {
    switch (isActive) {
      case false:
        return 'danger';
      case true:
        return 'success';
    }
  }

  getItemStatusString(isActive = false): string {
    switch (isActive) {
      case false:
        return this.translate.instant('GLOBAL.INACTIVE');
      case true:
        return this.translate.instant('GLOBAL.ACTIVE');
    }
  }
}
